import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { auth, db, storage } from "./firebase";
import { signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { ref, uploadBytesResumable, deleteObject, getDownloadURL } from "firebase/storage";
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, getDoc } from "firebase/firestore";
import { applyEventTheme } from "./themeUtils";
import logoUrl from "./assets/logo1.png";

export default function WeddingPhotoUploader() {
    const { eventId } = useParams();
    const [user, setUser] = useState(null);
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [existingPhotos, setExistingPhotos] = useState([]);
    const [showUploads, setShowUploads] = useState(false);
    const [progress, setProgress] = useState(0);
    const [showIOSNote, setShowIOSNote] = useState(false);
    const [eventConfig, setEventConfig] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [previewUrls, setPreviewUrls] = useState([]);
    const [videoThumbs, setVideoThumbs] = useState({});
    const [existingVideoThumbs, setExistingVideoThumbs] = useState({});
    const previewUrlsRef = useRef([]);
    const [showPedro, setShowPedro] = useState(false);
    const [pedroStarted, setPedroStarted] = useState(false);
    const audioRef = useRef(null);

    const fetchUserPhotos = useCallback(async (uid) => {
        const uploadsRef = collection(db, `weddings/${eventId}/uploads`);
        const q = query(uploadsRef, where("uid", "==", uid));
        const snap = await getDocs(q);

        const photos = [];

        for (const d of snap.docs) {
            const data = d.data();
            const path = `uploads/${eventId}/${data.uid}/${data.filename}`;
            const storageReference = ref(storage, path);

            try {
                const url = await getDownloadURL(storageReference);
                photos.push({ id: d.id, ...data, url });
            } catch (err) {
                console.warn(`⚠️ File not yet available or missing for ${data.filename}`);
                photos.push({ id: d.id, ...data, url: null, status: "processing" });
            }
        }

        setExistingPhotos(photos);
    }, [eventId]);

    const fetchEventConfig = useCallback(async () => {
        try {
            const eventDoc = await getDoc(doc(db, "events", eventId));

            if (eventDoc.exists()) {
                const config = eventDoc.data();

                if (config.eventDate && typeof config.eventDate.toDate === 'function') {
                    config.eventDate = config.eventDate.toDate();
                }

                setEventConfig(config);
                applyEventTheme(config);
            } else {
                setEventConfig({
                    coupleName: "Event Not Found",
                    eventDate: new Date(),
                    eventType: "Unknown Event",
                    theme: "classic",
                    background: { type: "color" },
                    showGuestNavbar: true
                });
                applyEventTheme({ theme: "classic" });
            }
        } catch (err) {
            console.error("❌ Error fetching event config:", err);

            const fallbackConfig = {
                coupleName: "Unable to Load Event",
                eventDate: new Date(),
                eventType: "Error Loading",
                theme: "classic",
                background: { type: "color" },
                showGuestNavbar: true
            };
            setEventConfig(fallbackConfig);
            applyEventTheme({ theme: "classic" });
        }
        setLoadingConfig(false);
    }, [eventId]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (u) => {
            if (u) {
                setUser(u);
                await fetchUserPhotos(u.uid);
            } else {
                const result = await signInAnonymously(auth);
                setUser(result.user);
                await fetchUserPhotos(result.user.uid);
            }
        });
        return () => unsubscribe();
    }, [eventId, fetchUserPhotos]);

    useEffect(() => {
        fetchEventConfig();
    }, [fetchEventConfig]);

    const generateVideoThumbnail = async (src) => {
        if (!src || typeof document === "undefined") return null;

        return new Promise((resolve) => {
            const video = document.createElement("video");
            video.preload = "metadata";
            video.muted = true;
            video.playsInline = true;
            video.onloadeddata = () => {
                try {
                    const canvas = document.createElement("canvas");
                    const width = video.videoWidth || 320;
                    const height = video.videoHeight || 240;
                    canvas.width = width;
                    canvas.height = height;

                    const context = canvas.getContext("2d");
                    context.drawImage(video, 0, 0, width, height);
                    resolve(canvas.toDataURL("image/jpeg", 0.8));
                } catch (error) {
                    resolve(null);
                }
            };
            video.onerror = () => resolve(null);
            video.src = src;
        });
    };

    useEffect(() => {
        const urls = files.map(file => URL.createObjectURL(file));
        previewUrlsRef.current = urls;
        setPreviewUrls(urls);
        return () => {
            urls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [files]);

    useEffect(() => {
        let isCancelled = false;

        const getThumbs = async () => {
            const nextThumbs = {};

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (!file || !file.type.startsWith("video/")) continue;

                const url = previewUrls[i];
                if (!url) continue;

                const poster = await generateVideoThumbnail(url);
                if (!isCancelled && poster) {
                    nextThumbs[i] = poster;
                }
            }

            if (!isCancelled) {
                setVideoThumbs(nextThumbs);
            }
        };

        getThumbs();

        return () => {
            isCancelled = true;
        };
    }, [files, previewUrls]);

    useEffect(() => {
        let isCancelled = false;

        const getExistingThumbs = async () => {
            const nextThumbs = {};

            for (const photo of existingPhotos) {
                if (!photo || !photo.filename || !photo.url) continue;
                const isVideo = /\.(mp4|mov|avi|mkv|quicktime)$/i.test(photo.filename);
                if (!isVideo) continue;

                const poster = await generateVideoThumbnail(photo.url);
                if (!isCancelled && poster) {
                    nextThumbs[photo.id || photo.filename] = poster;
                }
            }

            if (!isCancelled) {
                setExistingVideoThumbs(nextThumbs);
            }
        };

        getExistingThumbs();

        return () => {
            isCancelled = true;
        };
    }, [existingPhotos]);

    const handleFileChange = (e) => {
        const selected = [...e.target.files];
        if (selected.length + existingPhotos.length > 15) {
            alert("You can only upload up to 15 photos/videos.");
            return;
        }
        setFiles(selected);
    };

    const handleBrowseClick = () => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if (isIOS) {
            setShowIOSNote(true);
            setTimeout(() => setShowIOSNote(false), 6000);
        }
    };

    const convertHeicToJpeg = async (file) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth;
                    canvas.height = img.naturalHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    canvas.toBlob(
                        blob => {
                            if (blob) {
                                const newFile = new File([blob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
                                resolve(newFile);
                            } else {
                                reject(new Error('conversion blob null'));
                            }
                        },
                        'image/jpeg',
                        0.92
                    );
                } catch (err) {
                    reject(err);
                }
            };
            img.onerror = (e) => reject(e);
            img.src = URL.createObjectURL(file);
        });
    };

    const getBackgroundSlides = useCallback(() => {
        if (!eventConfig?.background) return [];

        const rawBackground = eventConfig.background;

        if (Array.isArray(rawBackground?.images) && rawBackground.images.length > 0) {
            return rawBackground.images
                .map((item) => (typeof item === "string" ? item : item?.url))
                .filter(Boolean);
        }

        if (Array.isArray(rawBackground?.imageUrls) && rawBackground.imageUrls.length > 0) {
            return rawBackground.imageUrls.filter(Boolean);
        }

        if (Array.isArray(rawBackground) && rawBackground.length > 0) {
            return rawBackground.map((item) => (typeof item === "string" ? item : item?.url)).filter(Boolean);
        }

        if (rawBackground?.url) {
            return [rawBackground.url];
        }

        return [];
    }, [eventConfig]);

    const backgroundSlides = getBackgroundSlides();
    const [slideIndex, setSlideIndex] = useState(0);
    const resolvedBackgroundType = useMemo(() => {
        const rawBackground = eventConfig?.background;
        if (!rawBackground) return "color";
        if (rawBackground.type) return rawBackground.type;
        if (Array.isArray(rawBackground.images) || Array.isArray(rawBackground.imageUrls) || Array.isArray(rawBackground)) return "image";
        if (rawBackground.url) {
            return /\.(mp4|webm|mov|m4v)$/i.test(rawBackground.url) ? "video" : "image";
        }
        return "color";
    }, [eventConfig]);

    useEffect(() => {
        if (backgroundSlides.length <= 1) {
            setSlideIndex(0);
            return;
        }

        const timer = setInterval(() => {
            setSlideIndex((prev) => (prev + 1) % backgroundSlides.length);
        }, 6500);

        return () => clearInterval(timer);
    }, [backgroundSlides]);

    const handleUpload = async () => {
        if (!user || files.length === 0) return;

        const uploadsRef = collection(db, `weddings/${eventId}/uploads`);
        setUploading(true);

        const errors = [];

        try {
            for (let i = 0; i < files.length; i++) {
                let file = files[i];

                if (!(file instanceof File) || !file.name || !file.size) {
                    errors.push(`❌ Skipped invalid file: ${file?.name || "(unknown)"}`);
                    continue;
                }

                const lowerName = file.name.toLowerCase();
                if (lowerName.endsWith('.heic') || file.type === 'image/heic') {
                    try {
                        file = await convertHeicToJpeg(file);
                    } catch (conversionError) {
                        console.warn('❌ HEIC conversion failed, skipping', file.name, conversionError);
                        errors.push(`Please convert ${file.name} to JPG/PNG before uploading.`);
                        continue;
                    }
                }

                const timestamp = Date.now();
                let ext = file.name.split(".").pop()?.toLowerCase() || "bin";
                if (ext === "quicktime") ext = "mov";

                const uniqueFilename = `${timestamp}-${i}.${ext}`;
                const path = `uploads/${eventId}/${user.uid}/${uniqueFilename}`;
                const storageReference = ref(storage, path);

                const uploadTask = uploadBytesResumable(storageReference, file);

                await new Promise((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        (snapshot) => {
                            const percent = Math.round(
                                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                            );
                            setProgress(percent);
                        },
                        (uploadErr) => {
                            const msg = `❌ Upload failed for ${file.name}: ${uploadErr.message}`;
                            errors.push(msg);
                            reject(uploadErr);
                        },
                        async () => {
                            try {
                                if (file.size > 5 * 1024 * 1024) {
                                    await new Promise(res => setTimeout(res, 1000));
                                }

                                await addDoc(uploadsRef, {
                                    uid: user.uid,
                                    filename: uniqueFilename,
                                    timestamp: new Date(),
                                });

                                resolve();
                            } catch (validationErr) {
                                const msg = `❌ Upload verified but file not accessible: ${file.name}\n${validationErr.message}`;
                                errors.push(msg);
                                reject(validationErr);
                            }
                        }
                    );
                });
            }

            if (errors.length === 0) {
                alert("✅ Upload complete!");
            } else {
                alert(`⚠️ Some issues occurred:\n\n${errors.join("\n")}`);
            }

            setFiles([]);
            await fetchUserPhotos(user.uid);
        } catch (err) {
            console.error("🔥 Upload handler error:", err);
            alert("Something went wrong during upload.");
        }

        setUploading(false);
    };

    const handleDelete = async (photo) => {
        if (!user) return;
        try {
            const path = `uploads/${eventId}/${user.uid}/${photo.filename}`;
            await deleteObject(ref(storage, path));
            await deleteDoc(doc(db, `weddings/${eventId}/uploads/${photo.id}`));
            await fetchUserPhotos(user.uid);
        } catch (err) {
            console.error("Delete error:", err);
            alert("Failed to delete photo.");
        }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm("Are you sure you want to delete all your uploaded photos?")) return;
        for (let photo of existingPhotos) {
            await handleDelete(photo);
        }
    };

    useEffect(() => {
        if (existingPhotos.length >= 15) {
            setShowPedro(true);
        }
    }, [existingPhotos]);

    const handlePedroClick = () => {
        if (!pedroStarted) {
            audioRef.current?.play();
            setPedroStarted(true);
        } else {
            audioRef.current?.pause();
            setShowPedro(false);
        }
    };

    const isCustomTheme2 = eventConfig?.theme === "customTheme2";
    const renderCustomTheme2Leaf = (side, style) => (
        <svg className={`custom-theme-2-leaf ${side}`} viewBox="0 0 140 240" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} aria-hidden="true">
            <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M72 230 C40 195, 90 165, 62 135 C25 95, 75 65, 62 15" />
                <path d="M62 40 C20 12, 10 60, 43 69 C65 75, 68 45, 49 46 C39 47, 40 58, 47 58" />
                <path d="M65 115 C105 75, 132 115, 105 138 C84 155, 73 125, 92 120 C102 117, 107 127, 99 131" />
                <path d="M66 180 C20 150, 5 202, 40 215 C60 222, 67 194, 49 191 C38 189, 35 203, 45 205" />
                <path d="M62 135 C20 115, 16 90, 30 82" />
                <path d="M68 165 C112 155, 124 180, 111 194" />
                <path d="M65 95 C91 68, 105 54, 98 36" />
                <path d="M30 82 Q15 69 20 55" />
                <path d="M111 194 Q127 207 119 220" />
                <path d="M98 36 Q108 23 101 12" />
            </g>
            <g fill="currentColor">
                <circle cx="62" cy="15" r="2.5" />
                <circle cx="72" cy="230" r="2.5" />
                <circle cx="20" cy="55" r="2" />
                <circle cx="101" cy="12" r="2" />
                <circle cx="119" cy="220" r="2" />
            </g>
        </svg>
    );

    return (
        <div className="uploader-page">
            {/* Background Layers */}
            {resolvedBackgroundType === "video" && eventConfig?.background?.url ? (
                <video autoPlay loop muted playsInline className="bg-video">
                    <source src={eventConfig.background.url} type="video/mp4" />
                </video>
            ) : null}

            {resolvedBackgroundType === "image" ? (
                backgroundSlides.length > 1 ? (
                    <div className="event-background-slideshow" aria-label="Background slideshow">
                        {backgroundSlides.map((slide, index) => (
                            <div
                                key={`${slide}-${index}`}
                                className={`event-background-slide ${index === slideIndex ? "active" : ""}`}
                                style={{ backgroundImage: `url(${slide})`, backgroundPosition: "center" }}
                            />
                        ))}
                    </div>
                ) : backgroundSlides.length === 1 ? (
                    <div className="event-background-static" style={{ backgroundImage: `url(${backgroundSlides[0]})`, backgroundPosition: "center" }} />
                ) : (
                    <div className="event-background-empty" />
                )
            ) : null}

            {/* Pedro Easter Egg */}
            {showPedro && (
                <div className="pedro-overlay" onClick={handlePedroClick}>
                    <audio ref={audioRef} src="/pedro.mp3?v=2" loop />
                    <div className={`pedro-glow-ring${pedroStarted ? " pedro-spinning" : ""}`}>
                        <div className="pedro-circle">
                            <img src="/pedro-raccoon.jpg" alt="Pedro" className="pedro-raccoon" />
                        </div>
                    </div>
                    {!pedroStarted ? (
                        <>
                            <p className="pedro-text">
                                {"Pedro Pedro Pedro...".split("").map((ch, i) => (
                                    <span key={i} style={{ animationDelay: `${i * 0.05}s` }}>{ch}</span>
                                ))}
                            </p>
                            <div className="pedro-start-btn">▶ Click pentru Pedro</div>
                        </>
                    ) : (
                        <>
                            <p className="pedro-text">
                                {"Pedro Pedro Pedro...".split("").map((ch, i) => (
                                    <span key={i} style={{ animationDelay: `${i * 0.05}s` }}>{ch}</span>
                                ))}
                            </p>
                            <p className="pedro-skip">Click oriunde pentru a închide</p>
                        </>
                    )}
                </div>
            )}

            {/* Topbar */}
            {eventConfig?.showGuestNavbar !== false && (
                <header className="es-topbar full-bleed classic-gold-theme">
                    <div className="es-topbar-inner">
                        <img src={logoUrl} alt="EventSnap" className="es-topbar-logo" />
                        <span className="es-topbar-title">
                            Event<span>Snap</span>
                        </span>
                    </div>
                </header>
            )}

            {/* Container vintage centrat vertical & orizontal, scalat discret */}
            <div 
                className="vintage-uploader-shell" 
                style={{ 
                    maxWidth: "520px", 
                    margin: "0 auto", 
                    padding: "18px 16px 32px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "calc(100vh - 75px)"
                }}
            >
                <div className="w-100 es-lead" style={{ margin: "0 0 4px" }}>
                    <div className="vintage-header-note" style={{ fontSize: "11px", letterSpacing: "0.26em", marginBottom: "8px" }}>
                        Collect every memory ♡
                    </div>
                    <h1 className="vintage-couple-name" style={{ fontSize: "clamp(46px, 9vw, 68px)", margin: "4px 0 6px" }}>
                        {loadingConfig ? "Loading..." : eventConfig?.coupleName || "Event"}
                    </h1>
                    <div className="vintage-divider" style={{ width: "min(75%, 320px)", margin: "0 auto 6px" }}>
                        <img 
                            src="/temp_rings.svg" 
                            alt="Wedding rings" 
                            className="vintage-rings" 
                        />
                    </div>
                    <div className="vintage-date" style={{ fontSize: "13px", letterSpacing: "0.16em", marginBottom: "14px" }}>
                        {loadingConfig ? "Loading..." : (() => {
                            let dateStr = "";
                            if (eventConfig?.eventDate) {
                                const dateObj = eventConfig.eventDate.toDate ? eventConfig.eventDate.toDate() : eventConfig.eventDate;
                                dateStr = dateObj.toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                });
                            } else {
                                dateStr = new Date().toLocaleDateString();
                            }
                            return `${eventConfig?.eventType || "Event"} - ${dateStr}`;
                        })()}
                    </div>
                </div>

                {/* Panoul vintage compactat */}
                <div 
                    className="vintage-panel" 
                    style={{ 
                        width: "100%", 
                        maxWidth: "460px", 
                        margin: "8px auto 0", 
                        padding: "24px 20px 20px",
                        borderRadius: "26px" 
                    }}
                >
                    {isCustomTheme2 ? (
                        <>
                            {renderCustomTheme2Leaf("left", { width: "70px", height: "120px", left: "-22px", bottom: "50px" })}
                            {renderCustomTheme2Leaf("right", { width: "70px", height: "120px", right: "-22px", bottom: "-12px" })}
                        </>
                    ) : (
                        <>
                            <svg className="vintage-leaf left" viewBox="0 0 120 210" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "68px", height: "120px", left: "-22px", bottom: "50px" }}>
                                <path d="M62 205 C60 156 52 111 38 70 C31 49 27 34 31 8" strokeWidth="1.2" />
                                <path d="M56 171 C42 160 30 149 18 137" strokeWidth="1" />
                                <path d="M51 147 C63 134 72 122 79 106" strokeWidth="1" />
                                <path d="M46 124 C32 113 22 100 13 87" strokeWidth="1" />
                                <path d="M41 99 C53 87 62 73 69 58" strokeWidth="1" />
                                <ellipse cx="23" cy="136" rx="5" ry="13" transform="rotate(-44 23 136)" strokeWidth="1" />
                                <ellipse cx="72" cy="111" rx="5" ry="13" transform="rotate(36 72 111)" strokeWidth="1" />
                                <ellipse cx="18" cy="87" rx="5" ry="13" transform="rotate(-48 18 87)" strokeWidth="1" />
                                <ellipse cx="64" cy="60" rx="5" ry="13" transform="rotate(34 64 60)" strokeWidth="1" />
                            </svg>
                            <svg className="vintage-leaf right" viewBox="0 0 120 210" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "68px", height: "120px", right: "-22px", bottom: "-12px" }}>
                                <path d="M62 205 C60 156 52 111 38 70 C31 49 27 34 31 8" strokeWidth="1.2" />
                                <path d="M56 171 C42 160 30 149 18 137" strokeWidth="1" />
                                <path d="M51 147 C63 134 72 122 79 106" strokeWidth="1" />
                                <path d="M46 124 C32 113 22 100 13 87" strokeWidth="1" />
                                <path d="M41 99 C53 87 62 73 69 58" strokeWidth="1" />
                                <ellipse cx="23" cy="136" rx="5" ry="13" transform="rotate(-44 23 136)" strokeWidth="1" />
                                <ellipse cx="72" cy="111" rx="5" ry="13" transform="rotate(36 72 111)" strokeWidth="1" />
                                <ellipse cx="18" cy="87" rx="5" ry="13" transform="rotate(-48 18 87)" strokeWidth="1" />
                                <ellipse cx="64" cy="60" rx="5" ry="13" transform="rotate(34 64 60)" strokeWidth="1" />
                            </svg>
                        </>
                    )}

                    <h1 className="vintage-panel-title" style={{ fontSize: "clamp(28px, 5.5vw, 38px)", marginBottom: "6px" }}>
                        Share your<br />favorite moments
                    </h1>
                    <p className="vintage-panel-subtitle" style={{ fontSize: "16px", lineHeight: "1.25", margin: "0 auto 16px" }}>
                        Upload the photos and videos<br />you captured today.
                    </p>

                    <div className="vintage-dropzone" style={{ padding: "16px 14px 14px", borderRadius: "18px", marginBottom: "10px" }}>
                        <svg className="vintage-cloud" viewBox="0 0 100 78" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "54px", height: "42px", margin: "0 auto 6px" }}>
                            <path d="M30 61 H75 C86 61 94 54 94 44 C94 34 86 27 76 27 C72 14 61 7 49 9 C37 10 28 18 26 29 C14 29 6 37 6 47 C6 55 13 61 30 61 Z" stroke="#F8F1DF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M49 66 V31 M49 31 L38 42 M49 31 L60 42" stroke="#F8F1DF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="vintage-drop-title" style={{ fontSize: "19px", margin: "2px 0 3px" }}>Drop your content here</div>
                        <div className="vintage-drop-small" style={{ fontSize: "13px", marginBottom: "10px" }}>or click to browse files</div>
                        <label className="vintage-btn browse" style={{ cursor: "pointer", fontSize: "18px", padding: "8px 14px", borderRadius: "14px", maxWidth: "220px", margin: "0 auto" }}>
                            Browse Files
                            <input
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                onChange={handleFileChange}
                                onClick={handleBrowseClick}
                                style={{ display: "none" }}
                            />
                        </label>
                    </div>

                    <button 
                        type="button" 
                        className="vintage-btn upload" 
                        onClick={handleUpload} 
                        disabled={uploading || files.length === 0 || existingPhotos.length + files.length > 15}
                        style={{ fontSize: "18px", padding: "9px 14px", borderRadius: "14px", marginTop: "8px" }}
                    >
                        {uploading ? "Uploading..." : "Upload Content"}
                    </button>
                    <button 
                        type="button" 
                        className="vintage-btn ghost" 
                        onClick={() => setShowUploads(!showUploads)}
                        style={{ fontSize: "18px", padding: "9px 14px", borderRadius: "14px", marginTop: "8px" }}
                    >
                        {showUploads ? "Hide Content" : "View Your Uploads"}
                    </button>

                    {showIOSNote && (
                        <div className="alert alert-warning small text-center mt-3 mb-0" role="alert">
                            📱 On iPhone: selecting videos may take a little longer before returning here.
                            Please wait — it’s normal.
                        </div>
                    )}

                    {files.length > 0 && (
                        <div className="selected-files-list mt-3">
                            <h6 className="text-center mb-2" style={{ color: "#f8f1df", fontSize: "0.9rem" }}>Selected Files:</h6>
                            <div className="row g-2 justify-content-center">
                                {files.map((file, index) => {
                                    const isImage = file.type.startsWith("image/");
                                    const isVideo = file.type.startsWith("video/");
                                    const url = previewUrls[index];
                                    const poster = videoThumbs[index];

                                    return (
                                        <div className="col-4 col-sm-3 position-relative" key={index}>
                                            <div className="selected-file-card" style={{ background: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.24)", padding: "0.25rem" }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                                                    className="btn btn-sm btn-close position-absolute top-0 end-0 m-1"
                                                    aria-label="Remove file"
                                                    style={{ filter: "invert(1)" }}
                                                ></button>

                                                {isImage ? (
                                                    <img src={url} alt={file.name} className="img-fluid" style={{ objectFit: "cover", height: "65px", width: "100%", borderRadius: "8px" }} />
                                                ) : isVideo ? (
                                                    <video src={url} poster={poster || undefined} className="img-fluid" style={{ height: "65px", width: "100%", objectFit: "cover", background: "rgba(255,255,255,0.12)", borderRadius: "8px" }} muted playsInline />
                                                ) : (
                                                    <div className="d-flex align-items-center justify-content-center" style={{ height: "65px", fontSize: "0.75rem", background: "rgba(255,255,255,0.12)", color: "#f8f1df", borderRadius: "8px" }}>
                                                        {file.name}
                                                    </div>
                                                )}

                                                <small className="d-block mt-1 text-truncate" style={{ color: "#f8f1df", fontSize: "0.75rem" }}>{file.name}</small>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {uploading && (
                        <div className="progress mt-3" style={{ height: "14px" }}>
                            <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${progress}%` }}>
                                {progress}%
                            </div>
                        </div>
                    )}

                    <p className="text-center mt-3 mb-0" style={{ color: "#f8f1df", fontSize: "0.9rem" }}>
                        Already uploaded: <strong>{existingPhotos.length}</strong> / 15
                    </p>
                </div>

                <div className="vintage-footer" style={{ margin: "16px auto 0", fontSize: "12px" }}>
                    <div className="vintage-heart" style={{ fontSize: "20px", marginBottom: "2px" }}>♡</div>
                    A special place<br />for our people
                </div>
            </div>

            {/* Gallery (shows below when clicked) */}
            {showUploads && (
                <div className="section-max mx-auto mb-5" style={{ position: "relative", zIndex: 2, maxWidth: "820px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 px-2">
                        <div className="gallery-title" style={{ color: "#f8f1df", fontSize: "1.2rem" }}>Your Gallery</div>
                        {existingPhotos.length > 0 && (
                            <button className="btn btn-danger-ghost btn-sm px-3" onClick={handleDeleteAll}>
                                🗑️ Delete All
                            </button>
                        )}
                    </div>
                    <div className="row g-3">
                        {existingPhotos.length === 0 ? (
                            <div className="col-12 text-center text-white-50">No uploads yet.</div>
                        ) : (
                            existingPhotos.map((photo) => (
                                <div key={photo.id} className="col-6 col-md-4 col-xl-3">
                                    <div className="gallery-card">
                                        {photo.url ? (
                                            photo.filename.match(/\.(mp4|mov|avi|mkv|quicktime)$/i) ? (
                                                <video src={photo.url} poster={existingVideoThumbs[photo.id || photo.filename]} className="gallery-img" muted playsInline />
                                            ) : (
                                                <img src={photo.url} alt="Uploaded media" className="gallery-img" />
                                            )
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center gallery-img text-muted">Processing...</div>
                                        )}
                                        <div className="p-2 d-flex justify-content-between align-items-center">
                                            <small className="text-truncate" style={{ maxWidth: "70%" }}>{photo.filename}</small>
                                            <button className="btn btn-sm btn-danger-ghost" onClick={() => handleDelete(photo)}>Delete</button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}